import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation, OnDestroy } from '@angular/core';

import { DialogService } from 'primeng/dynamicdialog';

import { DateService, HTTPService, HelperService, DataService, CalculationService, ValidationService } from 'app/services';
import { datetimeFormat } from 'app/constants/date-time-format';

import { find, findIndex } from 'lodash';
import { ServiceAuthorisationDialogComponent } from '../service-authorisation-dialog/service-authorisation-dialog.component';
import { Subscription } from 'rxjs';
import { CreateMyFleetCustomerDialogComponent } from '../create-myfleet-customer-dialog/create-myfleet-customer-dialog.component';
import { ConfirmDismissDialogComponent } from 'app/modules/shared/components';
import { WarrantyService } from 'app/services/warranty-service';

enum finalizeTypeEnum {
  FullyPaid = 'fullyPaid',
  AccountsReceivable = 'accountsReceivable',
  ThirdParty = 'thirdPartyAccounts',
}

interface SettlementOption {
  name: string;
  active: boolean;
  icon: string;
  value: string;
  disabledInfo?: string;
}

interface ThirdPartyAccount {
  _id: string;
  name: string;
  active: boolean;
  type: finalizeTypeEnum.ThirdParty;
  qboId?: string;
  isNationalFleetSale?: boolean;
}

interface SettlementDetailsComponentDetails {
  finalizeType: finalizeTypeEnum;
  thirdPartyAccount: ThirdPartyAccount;
  date: Date;
  time: Date;
}

interface SettlementDetailsComponentPayload {
  finalizeType: finalizeTypeEnum;
  thirdPartyAccount?: {
    id: string;
    name: string;
    type: string;
    qboId?: string;
  };
  finalizeDate: string;
  isPostToMyFleetCenter?: boolean;
}

interface Order {
  orderId: string;
  createdAt: Date;
  isPaidFull: boolean;
  isFinalized: boolean;
  finalizeDate: string;
  paid: number;
  unpaid: number;
  finalizeBy: string;
  depositRequest: any;
  isRequiredInfoMissing: boolean;
  isThirdPartyRo: boolean;
  thirdPartyInfo: any;
}

@Component({
  selector: 'app-invoice-settlement',
  templateUrl: './invoice-settlement.component.html',
  styleUrls: ['./invoice-settlement.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [DialogService],
})
export class InvoiceSettlementComponent implements OnInit, OnDestroy {
  @Input('orderId')
  set setOrderId(orderId: string) {
    if (this.order?.orderId !== orderId) {
      this.order = {
        orderId,
        isPaidFull: null,
        createdAt: null,
        isFinalized: false,
        finalizeDate: null,
        paid: 0,
        unpaid: 0,
        finalizeBy: '',
        isRequiredInfoMissing: false,
        depositRequest: null,
        isThirdPartyRo: false,
        thirdPartyInfo: {},
      };
      this.getOrderSettlementDetails(true);
    }
  }
  @Input('paid')
  set setPaid(paid: number) {
    if (this.order?.orderId && Number(paid || 0) !== Number(this.order?.paid || 0)) {
      this.getOrderSettlementDetails(false);
    }
  }
  @Input() isProcessing: boolean = false;
  @Input() hasUserPermissionToFinlize: boolean = false;
  @Input() estimate: any = {};
  @Input() isChargeAccountEnabled: boolean = false;
  @Input() customerId: string = '';
  @Input() selectedPaymentMethod: any = {};
  @Input() payment: any = {};

  @Output() setFinalizationState = new EventEmitter<any>();
  @Output() setLoadingState = new EventEmitter<any>();
  @Output() closeDialog = new EventEmitter<any>();
  @Output() refetchOrder = new EventEmitter<any>();
  @Output() onClickSettlementOption = new EventEmitter<any>();
  @Output() openReviewDialog = new EventEmitter<any>();
  @Output() openMarkRoThirdPartyDialog = new EventEmitter<any>();
  @Output() resetPaymentMethods = new EventEmitter<any>();

  chargeAccountTooltip = `Customer is non-charge. Please mark the customer as charge to finalize the RO as 'Send to AR'.`;
  chargeAccountTooltipUserNotPermited = `Customer is non-charge. Please contact store admin / manager to mark the customer as charge to finalize the RO as 'Send to AR'.`;
  settlementOptions: SettlementOption[] = [
    {
      name: 'Fully paid',
      active: false,
      icon: 'fal fa-money-bill',
      value: finalizeTypeEnum.FullyPaid,
      disabledInfo: `To finalize as 'Fully Paid', please pay the entire order amount using cash, card, check/bank, or credit memo.`,
    },
    {
      name: 'Send to AR',
      active: true,
      icon: 'fal fa-user-clock',
      value: finalizeTypeEnum.AccountsReceivable,
      disabledInfo: '',
    },
    {
      name: '3rd party account',
      active: false,
      icon: 'fal fa-store',
      value: finalizeTypeEnum.ThirdParty,
      disabledInfo: `To finalize as '3rd Party', the remaining balance must equal the order's total amount.`,
    },
  ];

  finalizeTypes = finalizeTypeEnum;
  settlementDetails: SettlementDetailsComponentDetails = {
    finalizeType: null,
    thirdPartyAccount: null,
    date: null,
    time: null,
  };
  order: Order;
  thirdPartyAccounts: ThirdPartyAccount[] = [];
  isFetching: boolean = true;
  isFinalizing: boolean = false;
  isDisabled: boolean = false;
  errors: any = {};
  validations: any = {};
  requiredInfoTooltip: string = '';
  subscription: Subscription;
  chargreAccountLimit = false;
  defaultSettlementOptions = [];
  customerHasChargeAccountEnabled: boolean = false;
  chargeAccountDetails: any = {};
  postToMyFleetCenter = false;
  isMyFleetCenterEnabled: boolean = this.dataService.isMyFleetCenterEnabled && this.dataService.isMyFleetCenterConnected;
  isMFCRequiredInfoMissing = false;
  MFCValidations: any = {};
  customer: any = {};
  vehicle: any = {};
  isSendPostGainInsight = false;
  isMissingDetail = false;
  requirements: string[] = [];
  isLoading: boolean = true;
  oldFleetNumber: string = '';
  updatedfleetNumber: string = '';

  constructor(
    private dialogService: DialogService,
    private dataService: DataService,
    private httpService: HTTPService,
    private helperService: HelperService,
    private dateService: DateService,
    private calculationService: CalculationService,
    private warrantyService: WarrantyService,
  ) { }

  async ngOnInit() {
    this.defaultSettlementOptions = this.helperService.deepCopy(this.settlementOptions);
    this.setDataListener();
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  setDataListener() {
    this.subscription = this.dataService.getData().subscribe(({ data }) => {
      if (data && data.key === 'reset-finalization-types') {
        this.handleChange('finalizeType', null);
      }

      if (data && data.key === 'sent-to-ar') {
        setTimeout(() => {
          if (data.data) {
            // Reset variable on payment applied
            this.chargreAccountLimit = false;
            this.settlementOptions = this.defaultSettlementOptions;
            this.getOrderSettlementDetails(false);
          }
        }, 100);
      }

      if (data.key === 'vehicle-update-finalize') {
        if (data?.data?.vehicle) {
          this.vehicle = data.data.vehicle;
          if (
            (this.settlementDetails.finalizeType === finalizeTypeEnum.AccountsReceivable || this.settlementDetails.finalizeType === finalizeTypeEnum.ThirdParty) &&
            this.customer?.isFleetCustomer &&
            this.vehicle?.isMFConnected &&
            this.customer?.isMFCustomer &&
            !this.isMFCRequiredInfoMissing
          ) {
            this.postToMyFleetCenter = true;
          }
        }
      }
    });
  }

  async getOrderSettlementDetails(fetchPayMethods = false, isFromClose?: boolean) {
    try {
      this.requiredInfoTooltip = '';
      this.isFetching = true;
      this.isLoading = true;
      this.setDisabledState();
      const data: any = await this.httpService.getOrderSettlementDetails(this.order.orderId, { fetchPayMethods: true });
      if (this.isMyFleetCenterEnabled && this.postToMyFleetCenter && this.isSendPostGainInsight) {
        this.sendPostGainInsight(data);
      }
      this.isSendPostGainInsight = false;

      const currentTime = this.dateService.getCurrentDateTimeTz(data.serverTime);
      this.setMfcFields(data);

      if (this.dataService.isPartWarrantiesEnabled) {
        try {
          const unassignedWarranties = await this.warrantyService.processUnassignedWarranties(data?._id, data?.services, '');
          if (unassignedWarranties?.unassignedServiceItems.length > 0) {
            data.validations = { ...data.validations, hasUnassignedWarranties: true };
            data.isRequiredInfoMissing = true;
          }
        }
        catch (err) {
          this.isLoading = false;
        }
        finally {
          this.isLoading = false;
        }
      } else {
        this.isLoading = false;
      }


      // Setting order details
      this.validations = data.validations;
      this.order = {
        orderId: data._id,
        isPaidFull: data.isPaidFull,
        createdAt: this.dateService.getCurrentDateTimeTz(new Date(data.backdatedCreationDate ?? data.createdAt).getTime()),
        isFinalized: data.isFinalized,
        finalizeDate: null,
        paid: this.calculationService.roundOff(data.total.paid),
        unpaid: this.calculationService.roundOff(data.total.unpaid),
        finalizeBy: '',
        isRequiredInfoMissing: data.isRequiredInfoMissing,
        depositRequest: data.depositRequest,
        isThirdPartyRo: data?.isThirdPartyRo || false,
        thirdPartyInfo: data?.thirdPartyInfo || {},
      };
      if (isFromClose && !data.isRequiredInfoMissing) {
        this.dataService.sendData(this.dataService.typePassData('info-updated', {}));
      }

      // Setting order finalization details if finalized
      if (this.order.isFinalized) {
        this.settlementDetails.finalizeType = data.finalizeType;

        if (data.finalizeDetails?.thirdPartyAccount?.paymentMethodId) {
          this.settlementDetails.thirdPartyAccount = {
            _id: data.finalizeDetails?.thirdPartyAccount?.paymentMethodId,
            name: data.finalizeDetails?.thirdPartyAccount?.name,
            active: true,
            type: finalizeTypeEnum.ThirdParty,
          };
        }

        if (data.finalizeDate) {
          const datetime = this.dateService.getCurrentDateTimeTz(new Date(data.finalizeDate).getTime());

          this.order.finalizeDate = this.dateService.formatDate(datetime, datetimeFormat.mmmdyyhhmmaa);
          this.settlementDetails.date = datetime;
          this.settlementDetails.time = datetime;
        }

        if (data.finalizeDetails?.finalizeBy?.name) {
          this.order.finalizeBy = data.finalizeDetails?.finalizeBy?.name;
        }
      } else {
        this.settlementDetails.date = currentTime;
        this.settlementDetails.time = currentTime;
      }

      if (data.paymentMethods?.length) {
        this.thirdPartyAccounts = data.paymentMethods.filter((method: any) => {
          if (method.type === 'THIRD_PARTY') {
            method.active = method.status;
            if (this.order.isThirdPartyRo && !this.order.isFinalized) {
              method.disabled = method._id !== this.order.thirdPartyInfo?.paymentMethodId;
            }
            return true;
          } else {
            return false;
          }
        });
      } else {
        this.thirdPartyAccounts = [];
      }

      if (data.isChargeAccountEnabled) {
        this.setChargeAccountInfo(data.chargeAccountDetails, data.customer);
      }

      // Fully paid is only enabled when order is fully paid
      const fullyPaidTypeIndex = findIndex(this.settlementOptions, { value: this.finalizeTypes.FullyPaid });
      this.settlementOptions[fullyPaidTypeIndex].active = !this.order.unpaid;

      // Third party is only enabled when order is completely unpaid
      const thirdPartyTypeIndex = findIndex(this.settlementOptions, { value: this.finalizeTypes.ThirdParty });
      //this.settlementOptions[thirdPartyTypeIndex].active = !this.order.paid;
      this.settlementOptions[thirdPartyTypeIndex].active = true;

      // De-select the selected option if after the payment selected option becomes inactive
      if (!this.order.isFinalized && this.settlementDetails.finalizeType) {
        const settlementOption = find(this.settlementOptions, { value: this.settlementDetails.finalizeType });

        if (!settlementOption.active) {
          this.settlementDetails.finalizeType = null;
          this.settlementDetails.thirdPartyAccount = null;
        }
      }

      if (data.validations) {
        this.requirements = [];
        this.requiredInfoTooltip = '';

        if (data.validations?.hasMissingTireStorageLocation) {
          this.helperService.showToaster('error', 'Please add a missing tire storage location to finalize order');
        } else if (data.validations?.hasPendingOrderedCart) {
          this.helperService.showToaster('error', 'Please mark all ordered carts delivered/canceled to finalize order');
        }

        if (data.validations?.hasMissingVIN) this.requirements.push('Missing VIN');
        if (data.validations?.hasMissingMileageIn && data.validations?.hasMissingMileageOut) {
          this.requirements.push(`Mileage in/out`);
        } else {
          if (data.validations?.hasMissingMileageIn) this.requirements.push(`Mileage`);
          else if (data.validations?.hasMissingMileageOut) this.requirements.push(`Mileage out`);
        }
        if (data.validations?.hasPendingServices) this.requirements.push('Authorizations');
        if (data.validations?.hasTireStorageLocationMissing) this.requirements.push('There are Tire Storages that are missing location');
        if (data.validations?.hasPendingOrderedCart) this.requirements.push("There are submitted Parts Orders which are not marked 'Delivered'");
        if (data.validations?.hasMissingDotNumbers) this.requirements.push('Missing DOT#');
        if (data.validations?.hasUnassignedClassification) this.requirements.push('There are items that require sales code assignment');
        if (data.validations?.hasUnassignedSalesCode) this.requirements.push('Missing Sales codes');
        if (data.validations?.hasUnassignedWarranties) this.requirements.push('Missing Warranty');
        if (data.validations?.limit) this.requirements.push('AR limit is not enaugh');

        if (this.requirements?.length) {
          this.requiredInfoTooltip = `${this.requirements.join(', ')} missing. Please add missing details to finalize RO.`;
        }
      }
    } catch (err) {
      this.helperService.showToaster('error', err);
    } finally {
      this.setDisabledState();
    }
  }

  setMfcFields(data) {
    this.customer = data?.customer?.customerId;
    if (!this.customer.fullName) {
      this.customer = {
        ...data?.customer,
        ...this.customer,
      };
    }

    this.oldFleetNumber = data?.fleetNumber || '';
    this.updatedfleetNumber = this.customer?.fleetNumber || '';

    this.vehicle = data?.vehicle?.vehicleId;
    this.isMFCRequiredInfoMissing = data?.isMFCRequiredInfoMissing;
    Object.keys(data.MFCValidations).forEach((key) => {
      if (key) {
        this.MFCValidations[key] = true;
      }
    });
    this.isFetching = false;
  }

  setDisabledState() {
    this.isDisabled = this.isFetching || this.isFinalizing || this.order.isFinalized;
  }

  handleChange(type: string, value: any) {
    if (type === 'finalizeType') {
      // in case of customer account not have charge account
      if (value === finalizeTypeEnum.AccountsReceivable && this.isChargeAccountEnabled) {
        if (!this.customerHasChargeAccountEnabled) {
          this.openCustomerSidebar();
          return;
        }
        if (this.chargeAccountDetails?.availableLimit < this.order.unpaid && !this.chargeAccountDetails.unlimited) {
          this.chargreAccountLimit = true;
        }
      }

      this.settlementDetails.finalizeType = value;
      this.settlementDetails.thirdPartyAccount = null;
    } else if (type === 'thirdPartyAccount') {
      this.settlementDetails.finalizeType = this.finalizeTypes.ThirdParty;
      this.settlementDetails.thirdPartyAccount = value;
      this.chargreAccountLimit = false;
    } else if (type === 'date') {
    } else if (type === 'time') {
    }

    this.errors[type] = null;

    if (type === 'finalizeType' && value === finalizeTypeEnum.AccountsReceivable) {
      this.onClickSettlementOption.emit(finalizeTypeEnum.AccountsReceivable);
    }

    if (value) {
      this.resetPaymentMethods.emit();
    }

    if (value === finalizeTypeEnum.AccountsReceivable || finalizeTypeEnum.ThirdParty) {
      if (this.customer?.isFleetCustomer && this.vehicle?.isMFConnected && this.customer?.isMFCustomer && !this.isMFCRequiredInfoMissing) {
        this.postToMyFleetCenter = true;
      }
    }
  }

  isValid(payload: SettlementDetailsComponentPayload) {
    let isValid = true;
    let { finalizeType, thirdPartyAccount, finalizeDate } = payload;

    if (!finalizeType) {
      isValid = false;
    }

    if (finalizeType === this.finalizeTypes.ThirdParty) {
      if (!thirdPartyAccount?.id) {
        isValid = false;
      }
    }

    if (!this.settlementDetails.date || !this.settlementDetails.time) {
      this.helperService.showToaster('error', 'Please select settlement date');
    } else {
      if (this.helperService.isAppleDevice()) {
        finalizeDate = this.dateService.madeDateStringIOSCompatible(finalizeDate);
      }

      if (new Date(this.order.createdAt).getTime() > new Date(finalizeDate).getTime()) {
        this.helperService.showToaster('error', `The payment cannot be received before RO was created ${this.dateService.formatDate(this.order.createdAt, datetimeFormat.mmmdyyhhmmaa)}`);
      }
    }

    return isValid;
  }

  makeSettlementPayload() {
    const { finalizeType, thirdPartyAccount, date, time } = this.settlementDetails;
    const finalizeDate = `${this.dateService.formatDate(date, datetimeFormat.yyymmdd)} ${this.dateService.formatDate(time, datetimeFormat.hhmm)}`;
    const payload: SettlementDetailsComponentPayload = {
      finalizeType: finalizeType,
      finalizeDate,
    };

    if (finalizeType === this.finalizeTypes.ThirdParty && thirdPartyAccount) {
      payload.thirdPartyAccount = {
        id: thirdPartyAccount._id,
        name: thirdPartyAccount.name,
        type: thirdPartyAccount.type,
        qboId: thirdPartyAccount.qboId,
      };
    }

    if (this.postToMyFleetCenter) {
      payload.isPostToMyFleetCenter = true;
    }

    return payload;
  }

  openAuthorizationDialog() {
    const { hasPendingServices, hasMissingVIN, hasMissingMileageIn, hasMissingMileageOut, hasMissingDotNumbers, hasUnassignedClassification, hasUnassignedWarranties } = this.validations;

    if (hasPendingServices || hasMissingVIN || hasMissingMileageIn || hasMissingMileageOut || hasMissingDotNumbers || hasUnassignedClassification || hasUnassignedWarranties) {
      const ref = this.dialogService.open(ServiceAuthorisationDialogComponent, {
        width: '1200px',
        styleClass: 'add-dialog authorization-dialog',
        showHeader: false,
        data: {
          data: {
            openFrom: 'finalization',
            isRevenueRecognitionSettleEnabled: true,
            estimateId: this.order.orderId,
            serviceData: {
              serviceTags: [],
              isManualPartOrderingEnabled: this.dataService.isManualPartOrderingEnabled,
              isPartsTechEnabled: this.dataService.isPartsTechEnabled,
              isNexpartEnabled: this.dataService.nexpartEnabled,
              depositRequest: this.order.depositRequest,
            },
            isAutoAuthorizeService: this.dataService.isAutoAuthorizeService && this.dataService.isAutoServiceAuthorizationEnabled,
          },
        },
      });

      const onClose = ref.onClose.subscribe(({ isSuccess, order }) => {
        this.getOrderSettlementDetails(false, true);
        this.refetchOrder.emit();
        onClose.unsubscribe();
      });
    } else {
      this.dataService.sendData(this.dataService.typePassData('settlement-validations', { validations: this.validations }));
      this.closeDialog.emit(this.validations);
    }
  }

  openConfirmDialog() {
    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      styleClass: 'finalization-confirmation-dialog',
      showHeader: false,
      data: {
        isCustomizedConfirmationBox: true,
        headerIconType: 'header-icon',
        isHTML: true,
        type: 'Confirm',
        styleClassHeading: 'font-18 font-700',
        styleClassCloseIcon: 'font-24 font-400 font-gray',
        headerIcon: 'fas fa-exclamation-triangle font-icon-red',
        title: 'Finalization Confirmation',
        message: `
        <div class="font-14 font-400">
        <div>
        You are about to finalize this repair order. Please note that once finalized, it cannot be posted to MyFleetCenter.
         </div>
         <div class="mt-3">
        Are you sure you want to proceed with finalization?
        </div>
        </div>`,
        button: {
          success: {
            title: 'Finalize without posting to MyFleetCenter',
            backgroundColor: '#D47877',
            styleClass: 'b-0 font-white',
          },
          cancel: { title: 'Cancel' },
        },
      },
    });

    const onClose = ref.onClose.subscribe((close: any) => {
      if (close.isSuccess) {
        this.handleSubmit(false);
      }
      onClose.unsubscribe();
    });
  }

  openFleetConfirmDialog() {
    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      styleClass: 'finalization-confirmation-dialog',
      showHeader: false,
      data: {
        isCustomizedConfirmationBox: true,
        headerIconType: 'header-icon',
        isHTML: true,
        type: 'Confirm',
        styleClassHeading: 'font-18 font-700',
        styleClassCloseIcon: 'font-24 font-400 font-gray',
        headerIcon: 'fas fa-exclamation-triangle font-icon-red',
        title: 'Fleet Number Mismatch',
        message: `
        <div class="font-14 font-400">
        <div>
        The fleet number on this Repair Order doesn’t match the customer’s fleet number.
         </div>
         <div class="mt-3">
        To post the invoice to MFC, the system will update the RO to use the correct fleet number (${this.updatedfleetNumber}).
        </div>
        </div>`,
        button: {
          success: {
            title: 'Confirm',
            backgroundColor: '#D47877',
            styleClass: 'b-0 font-white',
          },
          cancel: { title: 'Cancel' },
        },
      },
    });

    const onClose = ref.onClose.subscribe((close: any) => {
      if (close.isSuccess) {
        this.oldFleetNumber = this.updatedfleetNumber;
        this.handleSubmit(true);
      }
      onClose.unsubscribe();
    });
  }

  async handleSubmit(isForceUpdate = true) {
    if (
      this.oldFleetNumber && this.updatedfleetNumber &&
      this.oldFleetNumber !== this.updatedfleetNumber &&
      this.customer?.isFleetCustomer &&
      this.isMyFleetCenterEnabled &&
      this.postToMyFleetCenter
    ) {
      this.openFleetConfirmDialog();
      return;
    }

    if (this.isMyFleetCenterEnabled && this.postToMyFleetCenter && this.isMFCRequiredInfoMissing) {
      const ref = this.dialogService.open(ServiceAuthorisationDialogComponent, {
        width: '1200px',
        styleClass: 'add-dialog authorization-dialog',
        showHeader: false,
        data: {
          data: {
            estimateId: this.order.orderId,
            MFCValidations: this.MFCValidations,
            openFrom: 'finalize',
          },
        },
      });
      const onClose = ref.onClose.subscribe(({ isSuccess, data }) => {
        this.postToMyFleetCenter = data.postToMyFleetCenter;
        if (this.postToMyFleetCenter) {
          this.isMFCRequiredInfoMissing = false;
          this.isMissingDetail = true;
          this.handleSubmit();
        }
        onClose.unsubscribe();
      });
      return;
    }
    const payload = this.makeSettlementPayload();

    if (!this.isValid(payload) || this.isDisabled || this.isProcessing) return;

    if (
      isForceUpdate &&
      (this.settlementDetails.finalizeType === finalizeTypeEnum.AccountsReceivable || this.settlementDetails.finalizeType === finalizeTypeEnum.ThirdParty) &&
      this.customer?.isFleetCustomer &&
      this.isMyFleetCenterEnabled &&
      !this.postToMyFleetCenter
    ) {
      this.openConfirmDialog();
      return;
    }

    this.isFinalizing = true;
    this.setLoadingState.emit(this.isFinalizing);
    this.setDisabledState();

    try {
      const order: any = await this.httpService.finalizeOrder(this.order.orderId, payload);
      this.setFinalizationState.emit(order);
      this.openReviewDialog.emit(order);

      if (order?.finalizeType === finalizeTypeEnum.ThirdParty && (!order?.thirdPartyInfo?.poNumber || !order?.thirdPartyInfo?.claimNumber)) {
        this.dataService.sendData(this.dataService.typePassData('update-finalize-modal-data', { estimate: order }));
        if (order?.thirdPartyInfo) {
          order.thirdPartyInfo.isNationalFleetSale = this.settlementDetails.thirdPartyAccount.isNationalFleetSale;
        }
        this.openMarkRoThirdPartyDialog.emit(order);
      } else {
        this.closeDialog.emit(this.validations);
      }
      this.dataService.sendData(this.dataService.typePassData('payment-list', {}));
      this.isSendPostGainInsight = true;
      await this.getOrderSettlementDetails();
    } catch (err) {
      this.helperService.showToaster('error', err);
    } finally {
      this.isFinalizing = false;
      this.setLoadingState.emit(this.isFinalizing);
      this.setDisabledState();
      this.postToMyFleetCenter = false;
    }
  }

  sendPostGainInsight(order) {
    let event = '';
    let payload: any = {};

    if (!this.isMissingDetail) {
      event = 'Finalized and Posted to MyFleetCenter with all details already filled out';
    } else {
      event = 'Finalized and Posted to MyFleetCenter after filling out missing details';
    }

    payload = {
      ...payload,
      'Posted to MyFleetCenter date': order?.mfcInvoicePostedAt,
      'Fleet type (local / national)': order?.customer?.customerId?.MFC?.accountType,
      'RO#': order?.code,
      'Invoice#': order?.invCode,
      'Posted to MyFleetCenter (YES / NO)': this.postToMyFleetCenter ? 'YES' : 'NO',
      'Finalization date': order.finalizeDate,
      'Finalization type': order.finalizeType,
      'Paid (amount)': order?.total.paid,
      'Payment object(s)': {
        'Payment type': this.selectedPaymentMethod?.value,
        'Payment method': this.selectedPaymentMethod?.value,
        'Payment amount': this.payment.collectedAmount,
        'Payment date': this.payment.paymentDate,
      },
    };
    console.log(event, payload);
    this.helperService.postGainsightPXEvent(event, payload);
  }

  openCustomerSidebar() {
    const customer = this.helperService.deepCopy(this.estimate.customer);
    customer._id = this.estimate.customerId;
    customer.action = 'chargeAccount';
    customer.estimateId = this.estimate._id;
    customer.estimate = this.helperService.deepCopy(this.estimate);

    this.dataService.sendData(this.dataService.typeSidebar('customer', customer.customerId, customer.fullName, customer));
    // this.cdr.detectChanges();
  }

  isChargeAccountPermitted() {
    if (!this.isChargeAccountEnabled) {
      return false;
    }
    if (this.settlementDetails?.finalizeType === finalizeTypeEnum.AccountsReceivable) {
      if (this.isChargeAccountEnabled && !this.customerHasChargeAccountEnabled) {
        return true;
      }
    } else {
      return false;
    }
  }

  setChargeAccountInfo(chargeAccountDetails: any, customer: any) {
    if (chargeAccountDetails) {
      this.customerHasChargeAccountEnabled = true;
      this.chargeAccountDetails = {
        allowedLimit: chargeAccountDetails.allowedLimit,
        usedLimit: chargeAccountDetails.usedLimit,
        availableLimit: chargeAccountDetails.availableLimit,
        unlimited: chargeAccountDetails.unlimited,
        customerHasChargeAccount: true,
      };
      if (!this.chargeAccountDetails.unlimited && !this.chargeAccountDetails.allowedLimit) {
        if (customer?.isFleetCustomer) {
          this.chargeAccountDetails.allowedLimit = this.dataService.chargeAccounts?.fleetCustomerLimit?.value;
          this.chargeAccountDetails.unlimited = this.dataService.chargeAccounts?.fleetCustomerLimit?.unlimited;
        } else {
          this.chargeAccountDetails.allowedLimit = this.dataService.chargeAccounts?.regularCustomerLimit?.value;
          this.chargeAccountDetails.unlimited = this.dataService.chargeAccounts?.fleetCustomerLimit?.unlimited;
        }
      }
    }

    const chargeAccount = findIndex(this.settlementOptions, { value: this.finalizeTypes.AccountsReceivable });
    if (!this.customerHasChargeAccountEnabled) {
      if (!this.hasUserPermissionToFinlize) {
        this.settlementOptions[chargeAccount].active = false;
        this.settlementOptions[chargeAccount].disabledInfo = this.chargeAccountTooltipUserNotPermited;
      } else {
        this.settlementOptions[chargeAccount].active = true;
        this.settlementOptions[chargeAccount].disabledInfo = this.chargeAccountTooltip;
      }
    }
  }

  onCheckboxChange(event) {
    if (!this.getMyFleetCenterTooltip().disabled) {
      this.postToMyFleetCenter = !this.postToMyFleetCenter;
    }
  }

  connectToMyFleet() {
    const ref = this.dialogService.open(CreateMyFleetCustomerDialogComponent, {
      styleClass: 'add-dialog item-dialog new-part',
      showHeader: false,
      data: {
        fleetName: this.customer.fullName,
        fleetNumber: this.customer.fleetNumber,
        vehicle: this.vehicle,
        customer: this.customer,
        openFrom: 'finalize',
      },
    });
    const onClose = ref.onClose.subscribe((response: any) => {
      console.log(response);
      const { customer, vehicle } = response;
      if (customer) {
        this.customer = customer;
      }
      if (vehicle) {
        this.vehicle = vehicle;
      }
      this.postToMyFleetCenter = false;
      onClose.unsubscribe();
    });
  }

  getMyFleetCenterTooltip(): { disabled: boolean; tooltip: string } {
    const unpaidAmount = this.order?.unpaid || 0;
    const finalizationType = this.settlementDetails.finalizeType; // Assuming you have the finalization type
    const isVehicleConnected = this.vehicle?.isMFConnected;
    const isCustomerConnected = this.customer?.isMFCustomer;

    // Case 1: Unpaid amount less than 0
    if (unpaidAmount <= 0) {
      return {
        disabled: true,
        tooltip: 'To post an invoice to MyFleetCenter, remaining balance must be greater than 0',
      };
    }

    // Case 2: Finalization type is not 'Send to AR' and unpaid amount is greater than 0
    if (finalizationType !== finalizeTypeEnum.AccountsReceivable && finalizationType !== finalizeTypeEnum.ThirdParty && unpaidAmount > 0) {
      return {
        disabled: true,
        tooltip: 'To post an invoice to MyFleetCenter, please select the finalization type as "Send to AR" or "3rd Party"',
      };
    }

    // Case 3: Vehicle or customer is not connected to MyFleetCenter
    if (!isCustomerConnected && !isVehicleConnected) {
      return {
        disabled: true,
        tooltip: 'Please connect the customer and vehicle to MyFleetCenter by clicking “Connect to MyFleetCenter”',
      };
    }

    // Case 3: Vehicle or customer is not connected to MyFleetCenter
    if (!isCustomerConnected) {
      return {
        disabled: true,
        tooltip: 'Please connect the customer to MyFleetCenter by clicking “Connect to MyFleetCenter”',
      };
    }

    // Case 4: Only the vehicle is not connected
    if (!isVehicleConnected) {
      return {
        disabled: false,
        tooltip: 'We recommend connecting the vehicle to MyFleetCenter by clicking',
        // tooltip: 'Please connect the vehicle to MyFleetCenter by clicking “Connect to MyFleetCenter”',
      };
    }

    // If none of the conditions are met, enable the button
    return {
      disabled: false,
      tooltip: '',
    };
  }
}
